import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sanitizeEmail, sanitizeName, sanitizeCompany } from '../utils/inputSanitization';
import logger from '../utils/logger';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    company: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const { login, register, loading, error, errorObject, clearError, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      logger.log('🔐 Already authenticated, redirecting based on role:', user.role);
      
      // Redirect based on current user role
      if (user.role === 'admin') {
        navigate('/admin', { replace: true });
      } else if (user.role === 'consultant') {
        navigate('/consultant/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Set form mode based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/register') {
      setIsLogin(false);
    } else if (path === '/login' || path.includes('consultant')) {
      setIsLogin(true);
    }

    // Set consultant role for consultant paths
    if (path.includes('consultant')) {
      setFormData(prev => ({ ...prev, role: 'consultant' }));
    }
  }, [location.pathname]);

  // Clear errors when switching between login/register modes
  useEffect(() => {
    clearError();
  }, [isLogin]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let sanitizedValue = value;
    switch (name) {
      case 'email':
        sanitizedValue = value.replace(/[^a-zA-Z0-9@._-]/g, '').toLowerCase();
        break;
      case 'name':
        sanitizedValue = value.replace(/[^a-zA-Z\s\-']/g, '').slice(0, 100);
        break;
      case 'company':
        sanitizedValue = value.replace(/[<>"/]/g, '').slice(0, 200);
        break;
      default:
        sanitizedValue = value;
    }

    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearError();

    try {
      if (isLogin) {
        logger.log('🔐 Attempting login...');
        const result = await login(formData.email, formData.password);

        if (result?.success) {
          logger.log('✅ Login successful');
          logger.log('📦 Full result:', result);

          // CRITICAL: Use ONLY the fresh user data from login response
          const userData = result.user;
          const userRole = userData?.role || 'user';

          logger.log('👤 User data from login response:', userData);
          logger.log('🎭 User role from login response:', userRole);

          // Determine redirect based ONLY on fresh login data
          let redirectTo = '/dashboard';

          if (userRole === 'admin') {
            redirectTo = '/admin';
            logger.log('🔑 Admin user → /admin');
          } else if (userRole === 'consultant') {
            redirectTo = '/consultant/dashboard';
            logger.log('🔑 Consultant user → /consultant/dashboard');
          } else {
            redirectTo = '/dashboard';
            logger.log('🔑 Regular user → /dashboard');
          }

          logger.log('🚀 Redirecting to:', redirectTo);
          // Use replace to avoid back button issues and clear any cached state
          navigate(redirectTo, { replace: true, state: null });

        } else if (result?.data?.consultant_status || result?.consultant_status) {
          const consultantStatus = result.data?.consultant_status || result.consultant_status;
          const consultantEmail = result.data?.email || result.email || formData.email;
          
          logger.log('⚠️ Consultant status:', consultantStatus);
          
          clearError();
          
          navigate('/consultant-status', {
            state: {
              consultant_status: consultantStatus,
              email: consultantEmail
            }
          });
        } else if (result?.data?.requires_verification) {
          logger.log('📧 Verification required');
          navigate('/verify-email', {
            state: {
              email: formData.email,
              message: 'Please verify your email address before logging in.'
            }
          });
        } else {
          logger.error('❌ Login failed:', result?.error);
        }
      } else {
        logger.log('📝 Attempting registration...');
        const result = await register(formData);

        if (result?.success) {
          logger.log('✅ Registration successful');
          navigate('/verify-email', {
            state: {
              email: formData.email,
              message: 'Account created successfully! Please check your email for the verification code.'
            },
            replace: true
          });
        } else {
          logger.error('❌ Registration failed:', result?.error);
        }
      }
    } catch (error) {
      logger.error('💥 Form submission error:', error);
    }
  };

  const toggleMode = (e) => {
    e.preventDefault();
    clearError();

    if (isLogin) {
      navigate('/register');
    } else {
      navigate('/login');
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'consultant': return 'Energy Consultant';
      case 'user': return 'User';
      default: return role;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div>
          <div className="flex justify-center">
            <Link to="/" className="flex items-center">
              <div className="w-12 h-12 bg-emerald-600 dark:bg-emerald-700 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">RA</span>
              </div>
            </Link>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            {isLogin ? 'Sign in to your account' : 'Create your account'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            {isLogin ? (
              <>
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={toggleMode}
                  className="font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-500 dark:hover:text-emerald-300"
                >
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={toggleMode}
                  className="font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-500 dark:hover:text-emerald-300"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} noValidate>
          <div className="space-y-4">
            {/* Name field (registration only) */}
            {!isLogin && (
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required={!isLogin}
                  className="mt-1 appearance-none relative block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-lg focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>
            )}

            {/* Email field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-lg focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 focus:z-10 sm:text-sm"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>

            {/* Password field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  required
                  className="appearance-none relative block w-full px-3 py-3 pr-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-lg focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 11-4.243-4.243m4.243 4.243L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Role selection (registration only) */}
            {!isLogin && (
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Account Type
                </label>
                <select
                  id="role"
                  name="role"
                  className="mt-1 block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                  value={formData.role}
                  onChange={handleInputChange}
                >
                  <option value="user">User - General Access</option>
                  <option value="consultant">Energy Consultant - Professional Tools</option>
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formData.role === 'user'
                    ? 'Access to energy assessments and reports'
                    : 'Access to client management and professional tools'}
                </p>
              </div>
            )}

            {/* Company field (registration only, optional) */}
            {!isLogin && (
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Company (Optional)
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  className="mt-1 appearance-none relative block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-lg focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your company name"
                  value={formData.company}
                  onChange={handleInputChange}
                />
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400 dark:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  {error.includes('\n') ? (
                    <ul className="text-sm text-red-800 dark:text-red-300 space-y-1">
                      {error.split('\n').map((msg, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{msg}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Submit button */}
          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </div>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </button>
          </div>

          {/* Forgot Password Link (Login only) */}
          {isLogin && (
            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
              >
                Forgot your password?
              </Link>
            </div>
          )}

          {/* Back to homepage */}
          <div className="text-center">
            <Link to="/" className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 text-sm">
              ← Back to Homepage
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthPage;
